import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';
import RecipeViewer from './RecipeViewer';

const { FiArrowLeft, FiShoppingBag, FiCoffee, FiStar, FiLock, FiEye } = FiIcons;

function ShopScreen({ onNavigate }) {
  const { state, dispatch } = useGame();
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeViewer, setShowRecipeViewer] = useState(false);

  const shopItems = [
    {
      id: 'vanilla_syrup',
      name: 'Vanilla Syrup',
      description: 'Add sweet vanilla flavor to drinks',
      price: 50,
      type: 'ingredient',
      icon: FiCoffee,
      unlocked: state.unlockedItems.includes('vanilla_syrup')
    },
    {
      id: 'caramel_syrup',
      name: 'Caramel Syrup',
      description: 'Rich caramel sweetness',
      price: 75,
      type: 'ingredient',
      icon: FiCoffee,
      unlocked: state.unlockedItems.includes('caramel_syrup'),
      requirement: 'Level 3'
    },
    {
      id: 'matcha_powder',
      name: 'Premium Matcha Powder',
      description: 'Authentic Japanese matcha for tea drinks',
      price: 100,
      type: 'ingredient',
      icon: FiCoffee,
      unlocked: state.unlockedItems.includes('matcha_powder'),
      requirement: 'Level 4'
    },
    {
      id: 'ice',
      name: 'Ice Cubes',
      description: 'Fresh ice for cold beverages',
      price: 25,
      type: 'ingredient',
      icon: FiCoffee,
      unlocked: state.unlockedItems.includes('ice'),
      requirement: 'Level 4'
    },
    {
      id: 'espresso_upgrade',
      name: 'Premium Espresso Machine',
      description: 'Faster brewing and better quality',
      price: 200,
      type: 'equipment',
      icon: FiStar,
      unlocked: state.cafeUpgrades.espressoMachine >= 2,
      requirement: 'Level 5'
    },
    {
      id: 'milk_steamer_upgrade',
      name: 'Professional Milk Steamer',
      description: 'Perfect foam every time',
      price: 150,
      type: 'equipment',
      icon: FiStar,
      unlocked: state.cafeUpgrades.milkSteamer >= 2,
      requirement: 'Level 4'
    }
  ];

  const recipes = [
    {
      id: 'cappuccino',
      name: 'Cappuccino',
      description: 'Classic Italian coffee with foam',
      price: 100,
      unlocked: state.recipes.includes('cappuccino'),
      requirement: 'Level 2'
    },
    {
      id: 'macchiato',
      name: 'Macchiato',
      description: 'Espresso with a dollop of foam',
      price: 120,
      unlocked: state.recipes.includes('macchiato'),
      requirement: 'Level 3'
    },
    {
      id: 'mocha',
      name: 'Mocha',
      description: 'Coffee meets chocolate',
      price: 150,
      unlocked: state.recipes.includes('mocha'),
      requirement: 'Level 4'
    },
    {
      id: 'matcha_tea',
      name: 'Matcha Tea',
      description: 'Traditional Japanese green tea',
      price: 120,
      unlocked: state.recipes.includes('matcha_tea'),
      requirement: 'Level 4',
      category: 'matcha'
    },
    {
      id: 'matcha_latte',
      name: 'Matcha Latte',
      description: 'Creamy matcha with steamed milk',
      price: 140,
      unlocked: state.recipes.includes('matcha_latte'),
      requirement: 'Level 4',
      category: 'matcha'
    },
    {
      id: 'matcha_frappuccino',
      name: 'Matcha Frappuccino',
      description: 'Iced matcha blended drink',
      price: 160,
      unlocked: state.recipes.includes('matcha_frappuccino'),
      requirement: 'Level 5',
      category: 'matcha'
    }
  ];

  const canAfford = (price) => state.coins >= price;
  const meetsRequirement = (requirement) => {
    if (!requirement) return true;
    const reqLevel = parseInt(requirement.split(' ')[1]);
    return state.level >= reqLevel;
  };

  const purchaseItem = (item) => {
    if (!canAfford(item.price) || !meetsRequirement(item.requirement)) return;

    dispatch({ type: 'SPEND_COINS', payload: item.price });

    if (item.type === 'ingredient') {
      dispatch({ type: 'UNLOCK_ITEM', payload: item.id });
    } else if (item.type === 'equipment') {
      const equipmentName = item.id.replace('_upgrade', '');
      dispatch({
        type: 'UPGRADE_CAFE',
        payload: {
          item: equipmentName,
          level: state.cafeUpgrades[equipmentName] + 1
        }
      });
    }
  };

  const purchaseRecipe = (recipe) => {
    if (!canAfford(recipe.price) || !meetsRequirement(recipe.requirement)) return;

    dispatch({ type: 'SPEND_COINS', payload: recipe.price });
    dispatch({ type: 'UNLOCK_RECIPE', payload: recipe.id });
  };

  const handleRecipeView = (recipeId) => {
    setSelectedRecipe(recipeId);
    setShowRecipeViewer(true);
  };

  const regularRecipes = recipes.filter(recipe => recipe.category !== 'matcha');
  const matchaRecipes = recipes.filter(recipe => recipe.category === 'matcha');

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <motion.button
          className="bg-white/80 backdrop-blur-sm p-3 rounded-xl shadow-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onNavigate('menu')}
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl text-gray-600" />
        </motion.button>
        
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiShoppingBag} className="mr-2 text-amber-600" />
            Coffee Shop
          </h1>
        </div>
        
        <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg">
          <p className="text-lg font-bold text-green-600">${state.coins}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-8">
        {/* Regular Recipes Section */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Coffee Recipes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {regularRecipes.map((recipe) => {
              const canPurchase = canAfford(recipe.price) && meetsRequirement(recipe.requirement) && !recipe.unlocked;

              return (
                <motion.div
                  key={recipe.id}
                  className={`bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg ${
                    recipe.unlocked ? 'border-2 border-green-400' : ''
                  }`}
                  whileHover={{ scale: 1.02, y: -2 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl">☕</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{recipe.name}</h3>
                    <p className="text-sm text-gray-600">{recipe.description}</p>
                  </div>

                  {recipe.unlocked ? (
                    <div className="space-y-2">
                      <div className="text-center">
                        <span className="bg-green-100 text-green-600 px-4 py-2 rounded-full font-medium">
                          ✓ Unlocked
                        </span>
                      </div>
                      <motion.button
                        className="w-full bg-blue-100 text-blue-600 py-2 rounded-xl font-medium hover:bg-blue-200 transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleRecipeView(recipe.id)}
                      >
                        <SafeIcon icon={FiEye} className="inline mr-2" />
                        View Recipe
                      </motion.button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-green-600">${recipe.price}</span>
                        {recipe.requirement && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {recipe.requirement}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <motion.button
                          className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                            canPurchase
                              ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                          whileHover={canPurchase ? { scale: 1.02 } : {}}
                          whileTap={canPurchase ? { scale: 0.98 } : {}}
                          onClick={() => canPurchase && purchaseRecipe(recipe)}
                          disabled={!canPurchase}
                        >
                          {!meetsRequirement(recipe.requirement) ? (
                            <>
                              <SafeIcon icon={FiLock} className="inline mr-2" />
                              Locked
                            </>
                          ) : !canAfford(recipe.price) ? (
                            'Not enough coins'
                          ) : (
                            'Learn Recipe'
                          )}
                        </motion.button>
                        <motion.button
                          className="bg-blue-100 text-blue-600 px-3 py-3 rounded-xl hover:bg-blue-200 transition-colors"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleRecipeView(recipe.id)}
                        >
                          <SafeIcon icon={FiEye} className="text-lg" />
                        </motion.button>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Matcha Recipes Section */}
        {state.level >= 4 && (
          <section>
            <div className="flex items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Matcha Collection</h2>
              <span className="ml-3 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                🍃 New at Level 4
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {matchaRecipes.map((recipe) => {
                const canPurchase = canAfford(recipe.price) && meetsRequirement(recipe.requirement) && !recipe.unlocked;

                return (
                  <motion.div
                    key={recipe.id}
                    className={`bg-gradient-to-br from-green-50 to-green-100 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 ${
                      recipe.unlocked ? 'border-green-400' : 'border-green-200'
                    }`}
                    whileHover={{ scale: 1.02, y: -2 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="text-center mb-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-2xl">🍃</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">{recipe.name}</h3>
                      <p className="text-sm text-gray-600">{recipe.description}</p>
                    </div>

                    {recipe.unlocked ? (
                      <div className="space-y-2">
                        <div className="text-center">
                          <span className="bg-green-200 text-green-700 px-4 py-2 rounded-full font-medium">
                            ✓ Mastered
                          </span>
                        </div>
                        <motion.button
                          className="w-full bg-green-200 text-green-700 py-2 rounded-xl font-medium hover:bg-green-300 transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleRecipeView(recipe.id)}
                        >
                          <SafeIcon icon={FiEye} className="inline mr-2" />
                          View Recipe
                        </motion.button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-bold text-green-600">${recipe.price}</span>
                          {recipe.requirement && (
                            <span className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded">
                              {recipe.requirement}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <motion.button
                            className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                              canPurchase
                                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                            whileHover={canPurchase ? { scale: 1.02 } : {}}
                            whileTap={canPurchase ? { scale: 0.98 } : {}}
                            onClick={() => canPurchase && purchaseRecipe(recipe)}
                            disabled={!canPurchase}
                          >
                            {!meetsRequirement(recipe.requirement) ? (
                              <>
                                <SafeIcon icon={FiLock} className="inline mr-2" />
                                Locked
                              </>
                            ) : !canAfford(recipe.price) ? (
                              'Not enough coins'
                            ) : (
                              'Learn Recipe'
                            )}
                          </motion.button>
                          <motion.button
                            className="bg-green-200 text-green-700 px-3 py-3 rounded-xl hover:bg-green-300 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleRecipeView(recipe.id)}
                          >
                            <SafeIcon icon={FiEye} className="text-lg" />
                          </motion.button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* Equipment & Ingredients Section */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Equipment & Ingredients</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shopItems.map((item) => {
              const canPurchase = canAfford(item.price) && meetsRequirement(item.requirement) && !item.unlocked;

              return (
                <motion.div
                  key={item.id}
                  className={`bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg ${
                    item.unlocked ? 'border-2 border-green-400' : ''
                  } ${item.id === 'matcha_powder' || item.id === 'ice' ? 'border-green-200' : ''}`}
                  whileHover={{ scale: 1.02, y: -2 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="text-center mb-4">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${
                        item.id === 'matcha_powder' || item.id === 'ice'
                          ? 'bg-gradient-to-r from-green-400 to-green-600'
                          : 'bg-gradient-to-r from-blue-400 to-purple-500'
                      }`}
                    >
                      <SafeIcon icon={item.icon} className="text-2xl text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{item.name}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>

                  {item.unlocked ? (
                    <div className="text-center">
                      <span className="bg-green-100 text-green-600 px-4 py-2 rounded-full font-medium">
                        ✓ Owned
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-green-600">${item.price}</span>
                        {item.requirement && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {item.requirement}
                          </span>
                        )}
                      </div>
                      <motion.button
                        className={`w-full py-3 rounded-xl font-bold transition-all ${
                          canPurchase
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                        whileHover={canPurchase ? { scale: 1.02 } : {}}
                        whileTap={canPurchase ? { scale: 0.98 } : {}}
                        onClick={() => canPurchase && purchaseItem(item)}
                        disabled={!canPurchase}
                      >
                        {!meetsRequirement(item.requirement) ? (
                          <>
                            <SafeIcon icon={FiLock} className="inline mr-2" />
                            Locked
                          </>
                        ) : !canAfford(item.price) ? (
                          'Not enough coins'
                        ) : (
                          'Purchase'
                        )}
                      </motion.button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Recipe Viewer Modal */}
      <RecipeViewer
        isOpen={showRecipeViewer}
        onClose={() => setShowRecipeViewer(false)}
        recipeId={selectedRecipe}
      />
    </div>
  );
}

export default ShopScreen;